import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  IMapLibrePolygonEditorHandle,
  IMapLibreTraceControls,
  MapLibrePolygonEditor,
  MapLibrePolygonEditorToolbar,
  MuiButton,
  TPolygonEditorMode,
} from "@ryde/controls";
import {
  convertGeoAreasGeomToGeoJson,
  DrawerType,
  IGeoJSON,
  ImportAreaTypeEnum,
  useDrawers,
} from "@ryde/shared";

import { LayoutRight } from "../right";
import { IPolygonBlockProps } from "./polygon-block";
import {
  PolygonEditorMapStyled,
  PolygonEditorStyled,
} from "./polygon-block-sidebar.styled";
import {
  POLYGON_MAP_DEFAULT_ZOOM,
  POLYGON_MAP_EDITOR_COLOR,
  POLYGON_MAP_FILL_OPACITY,
  POLYGON_MAP_FIT_BOUNDS_PADDING_PX,
  POLYGON_MAP_WEIGHT,
} from "./polygon-map.constants";

interface IPolygonBlockSidebar extends IPolygonBlockProps {
  isMapOpen: boolean;
  enabledImportAreas?: boolean;
  setIsMapOpen: (value: boolean) => void;
}

const EMPTY_GEO_JSON: IGeoJSON = { type: "FeatureCollection", features: [] };

export const PolygonBlockSidebar: FC<IPolygonBlockSidebar> = ({
  value,
  enabledImportAreas,
  onChange,
  setIsMapOpen,
}) => {
  const { t } = useTranslation();
  const { openDrawer } = useDrawers();
  const editorRef = useRef<IMapLibrePolygonEditorHandle>();
  const traceControlsRef = useRef<IMapLibreTraceControls>();
  // No tool selected on open, and every tool returns here — geoman's toolbar rested the same way.
  const [mode, setMode] = useState<TPolygonEditorMode>("none");

  const handleMapClose = useEvent((): void => {
    setIsMapOpen(false);
  });

  const handleSave = useEvent((): void => {
    onChange?.(editorRef.current?.getGeoJson() ?? EMPTY_GEO_JSON);
    setIsMapOpen(false);
  });

  const handleClearAll = useEvent((): void => {
    editorRef.current?.clearAll();
  });

  const handleGestureComplete = useEvent((): void => setMode("none"));

  const handleImportPolygons = useEvent((): void => {
    openDrawer({
      drawerProps: {
        type: DrawerType.ImportAreas,
        importType: ImportAreaTypeEnum.IMPORT_AREAS_POLYGONS,
        title: t("area.formLabel.importPolygons"),
        onSelectPolygons: data =>
          editorRef.current?.addFeatures(convertGeoAreasGeomToGeoJson(data)),
      },
    });
  });

  return (
    <LayoutRight
      headerTitle={t("area.polygonEditor")}
      showFooter
      closeClick={handleMapClose}
      saveClick={handleSave}
      showBackgroundImage={false}
      positionFixed={true}
    >
      <PolygonEditorStyled>
        <div className="toolbar">
          <MuiButton muiVariant="outlined" onClick={handleClearAll}>
            {t("area.clearAllPolygons")}
          </MuiButton>
          {enabledImportAreas && (
            <MuiButton muiVariant="outlined" onClick={handleImportPolygons}>
              {t("area.formLabel.importPolygons")}
            </MuiButton>
          )}
        </div>
        <div className="mapArea">
          <PolygonEditorMapStyled
            initialZoom={POLYGON_MAP_DEFAULT_ZOOM}
            header={
              <MapLibrePolygonEditorToolbar
                mode={mode}
                onModeChange={setMode}
                traceControlsRef={traceControlsRef}
              />
            }
          >
            <MapLibrePolygonEditor
              value={value}
              handleRef={editorRef}
              mode={mode}
              color={POLYGON_MAP_EDITOR_COLOR}
              fillOpacity={POLYGON_MAP_FILL_OPACITY}
              weight={POLYGON_MAP_WEIGHT}
              fitPadding={POLYGON_MAP_FIT_BOUNDS_PADDING_PX}
              onGestureComplete={handleGestureComplete}
              traceControlsRef={traceControlsRef}
            />
          </PolygonEditorMapStyled>
        </div>
      </PolygonEditorStyled>
    </LayoutRight>
  );
};
